import { useEffect, useState } from 'react';

import { cn } from '../lib/cn.js';

export type SyncState =
  | 'idle'
  | 'saving'
  | 'saved'
  | 'queued'
  | 'syncing'
  | 'conflict'
  | 'offline';

interface SyncIndicatorProps {
  state: SyncState;
  /** Optional label override; the default is derived from `state`. */
  label?: string;
}

/**
 * Bottom-right floating indicator described in DESIGN_SYSTEM.md §12. The
 * "saved" state auto-fades to "idle" after 2s, matching the spec. A
 * persistent "queued" state stays visible until reconnected — wiring of
 * the actual offline queue arrives in Phase 10.
 */
export function SyncIndicator({ state, label }: SyncIndicatorProps) {
  const [visible, setVisible] = useState(state !== 'idle');

  useEffect(() => {
    if (state === 'idle') {
      setVisible(false);
      return;
    }
    setVisible(true);
    if (state === 'saved') {
      const t = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state]);

  if (!visible) return null;

  const palette = {
    saving: { dot: 'bg-info animate-pulse-dot', text: 'Saving…' },
    saved: { dot: 'bg-success', text: 'Saved' },
    syncing: { dot: 'bg-info animate-pulse-dot', text: 'Syncing…' },
    queued: { dot: 'bg-warning', text: 'Offline — will sync when reconnected' },
    offline: { dot: 'bg-warning', text: 'Offline' },
    conflict: { dot: 'bg-danger', text: 'Sync conflicts need review' },
    idle: { dot: '', text: '' },
  }[state];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed bottom-4 right-4 z-40 flex items-center gap-2',
        'rounded-md border border-border bg-surface-elevated px-3 py-2 text-xs font-medium text-fg shadow-md',
        'animate-fade-in',
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', palette.dot)} aria-hidden />
      <span>{label ?? palette.text}</span>
    </div>
  );
}
